/**
 * The ranking's result, live: texts (or prompt groups) best-first with a
 * column per metric the formula reads, filtered (reuse pool · reusable ·
 * all) and searchable; a row opens on how its score is made. Columns and
 * the math come from the formula that was scored, so they always match the
 * numbers; the previous result stays on screen while the next one loads.
 * It fills the pop-up's live panel: the head stays, the rows scroll down to
 * the panel's bottom edge (user, 12/10/2026: "show the most text").
 * While no text is ranked, the example's made-up texts take the list (with
 * its strip and, when the campaign has texts of its own, a switch).
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useMemo, useState } from 'react';
import { type DropGenTextRankingPreview, hourRangeLabel } from '@trafficpulse/shared';

import { cn } from '@/lib/utils';

import { TagColorsProvider } from '../../../rules/TagChips';
import { TagReadNotes } from '../../../rules/TagReadNotes';
import { ExampleBanner } from './example/ExampleBanner';
import { sourceView, type ResultsSource } from './example/example-view';
import { rankingColumns } from './ranking-columns';
import { ResultsHeader, type ResultsView } from './ResultsHeader';
import { barScale, filterTexts, type TextFilter } from './results-grid';
import { GroupRow } from './rows/GroupRow';
import { TextRow } from './rows/TextRow';

interface Props {
  data: DropGenTextRankingPreview;
  loading: boolean;
  onToggleExclude: (templateId: string) => void;
  /** The "Not enough data" rule's number (null = none) — later ranges thinner than it get underlined. */
  floorVisits: number | null;
}

export const RankedResults: FC<Props> = ({ data: read, loading, onToggleExclude, floorVisits }) => {
  const [source, setSource] = useState<ResultsSource>(read.rankedTexts === 0 ? 'example' : 'yours');
  const data = sourceView(read, source);
  const example = data !== read;
  const [view, setView] = useState<ResultsView>('texts');
  // Reusable first: most ranked texts were written outside TrafficPulse and
  // cannot be reused (26 of the top 200 on campaign 8132, 10/10/2026).
  const [filter, setFilter] = useState<TextFilter>('reusable');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const { columns, hidden } = useMemo(() => rankingColumns(data.formula), [data.formula]);
  const texts = useMemo(() => filterTexts(data.texts, filter, search), [data.texts, filter, search]);
  const groups = useMemo(() => data.groups.filter((g) => !search.trim() || g.groupName.toLowerCase().includes(search.trim().toLowerCase())), [data.groups, search]);
  const bar = barScale(view === 'texts' ? data.texts.map((t) => t.score) : data.groups.map((g) => g.score));
  const floor = { minVolume: floorVisits, rangeLabel: hourRangeLabel(data.primaryRangeKey), primaryRangeKey: data.primaryRangeKey };
  const toggle = (key: string) => setOpen((cur) => (cur === key ? null : key));
  const rows = view === 'texts' ? texts.length : groups.length;
  return (
    <TagColorsProvider colors={data.tagColors}>
      <div className={cn('flex min-h-0 flex-1 flex-col transition-opacity', loading && 'opacity-70')}>
      <TagReadNotes read={data} surface="texts" className="border-b border-border px-3 py-2" />
      {read.example && (example || read.rankedTexts === 0) ? <ExampleBanner source={example ? 'example' : 'yours'} onSource={setSource} yours={read.totalTexts} /> : null}
      <ResultsHeader data={data} loading={loading} columns={columns} view={view} onView={(v) => { setView(v); setOpen(null); }} filter={filter} onFilter={setFilter} search={search} onSearch={setSearch} />
      {rows === 0 ? (
        <div className="flex flex-col items-start gap-2 px-4 py-5">
          <p className="text-sm text-muted-foreground">{emptyLine(view, filter, search)}</p>
          {read.example && !example && !search.trim() ? (
            <button type="button" className="text-sm font-medium text-primary hover:underline" onClick={() => { setFilter('all'); setSearch(''); setSource('example'); }}>
              See an example — made-up texts on this formula
            </button>
          ) : null}
        </div>
      ) : (
        <ul className="min-h-0 flex-1 divide-y divide-border overflow-y-auto [scrollbar-gutter:stable]">
          {view === 'texts'
            ? texts.map((t) => (
                <TextRow key={t.templateId} t={t} columns={columns} formula={data.formula} bar={bar(t.score)} open={open === t.templateId} onOpen={() => toggle(t.templateId)} onToggleExclude={example ? undefined : () => onToggleExclude(t.templateId)} floor={floor} />
              ))
            : groups.map((g) => (
                <GroupRow key={g.groupId} g={g} columns={columns} formula={data.formula} bar={bar(g.score)} open={open === g.groupId} onOpen={() => toggle(g.groupId)} floor={floor} />
              ))}
        </ul>
      )}
      {hidden > 0 ? <p className="border-t border-border px-3 py-1.5 text-[11px] text-muted-foreground">+{hidden} more metric{hidden > 1 ? 's' : ''} the formula reads — open a row to see every value.</p> : null}
      </div>
    </TagColorsProvider>
  );
};

function emptyLine(view: ResultsView, filter: TextFilter, search: string): string {
  if (search.trim()) return `Nothing matches "${search.trim()}".`;
  if (view === 'groups') return "No prompt group yet — the catalog knows none of these texts' groups.";
  if (filter === 'all') return 'No text has been sent on this campaign group in the ranked range yet.';
  return 'No text here yet — lower "Not enough data" in Rules, widen the range, or look at All.';
}
